import React, { useState, useEffect } from 'react';
import api from '../services/api';

const UserViewPreferences = ({ isOpen, onClose, onSave }) => {
  const [devices, setDevices] = useState([]);
  const [viewPreferences, setViewPreferences] = useState({});
  const [commandPreferences, setCommandPreferences] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedDevices, setExpandedDevices] = useState({});

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Cargar dispositivos disponibles y preferencias actuales en paralelo
      const [devicesRes, prefsRes] = await Promise.all([
        api.get('/api/users/me/available-devices'),
        api.get('/api/users/me/view-preferences')
      ]);

      const loadedDevices = devicesRes.data.devices || [];
      console.log('👁️ UserViewPreferences - Dispositivos cargados:', loadedDevices.length);
      loadedDevices.forEach(d => {
        console.log(`  📱 ${d.name}: ${d.magnitudes?.length || 0} magnitudes, ${d.commands?.length || 0} comandos`);
        if (d.commands?.length > 0) {
          console.log('    🎮 Comandos:', d.commands.map(c => c.name));
        }
      });
      setDevices(loadedDevices);
      setViewPreferences(prefsRes.data.viewPreferences || {});
      setCommandPreferences(prefsRes.data.commandPreferences || {});

      // Expandir todos los dispositivos por defecto
      const expanded = {};
      (devicesRes.data.devices || []).forEach(d => {
        expanded[d._id] = true;
      });
      setExpandedDevices(expanded);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDevice = (deviceId) => {
    setExpandedDevices(prev => ({
      ...prev,
      [deviceId]: !prev[deviceId]
    }));
  };

  const isMagnitudeSelected = (deviceId, magnitudeName) => {
    const devicePrefs = viewPreferences[deviceId];
    // Si no hay preferencias para este dispositivo, se considera que todas están seleccionadas
    if (!devicePrefs) {
      return true;
    }
    // Si el array existe pero está vacío, ninguna está seleccionada
    if (devicePrefs.length === 0) {
      return false;
    }
    return devicePrefs.includes(magnitudeName);
  };

  const toggleMagnitude = (deviceId, magnitudeName) => {
    const device = devices.find(d => d._id === deviceId);
    const allMagnitudes = device?.magnitudes?.map(m => m.name) || [];

    setViewPreferences(prev => {
      const newPrefs = JSON.parse(JSON.stringify(prev)); // Deep clone
      const currentPrefs = newPrefs[deviceId];

      // Si no hay preferencias guardadas, significa que todas están visibles
      // Al hacer clic, queremos quitar esta magnitud (deseleccionar)
      if (!currentPrefs) {
        // Crear array con todas EXCEPTO la que se deseleccionó
        newPrefs[deviceId] = allMagnitudes.filter(m => m !== magnitudeName);
      } else {
        // Ya hay preferencias guardadas
        if (currentPrefs.includes(magnitudeName)) {
          // Está seleccionada -> quitarla
          newPrefs[deviceId] = currentPrefs.filter(m => m !== magnitudeName);
        } else {
          // No está seleccionada -> agregarla
          newPrefs[deviceId] = [...currentPrefs, magnitudeName];
        }
      }

      // Si todas las magnitudes están seleccionadas, eliminar la entrada
      // para volver al estado "mostrar todas por defecto"
      if (newPrefs[deviceId] && newPrefs[deviceId].length === allMagnitudes.length) {
        delete newPrefs[deviceId];
      }

      return newPrefs;
    });
  };

  const selectAllMagnitudes = (deviceId) => {
    setViewPreferences(prev => {
      const newPrefs = { ...prev };
      delete newPrefs[deviceId]; // Sin preferencias = mostrar todas
      return newPrefs;
    });
  };

  const deselectAllMagnitudes = (deviceId) => {
    setViewPreferences(prev => {
      const newPrefs = { ...prev };
      newPrefs[deviceId] = []; // Array vacío = no mostrar ninguna
      return newPrefs;
    });
  };

  // ===== FUNCIONES PARA COMANDOS =====
  const isCommandSelected = (deviceId, commandName) => {
    const devicePrefs = commandPreferences[deviceId];
    // Si no hay preferencias para este dispositivo, se considera que todos están seleccionados
    if (!devicePrefs) {
      return true;
    }
    // Si el array existe pero está vacío, ninguno está seleccionado
    if (devicePrefs.length === 0) {
      return false;
    }
    return devicePrefs.includes(commandName);
  };

  const toggleCommand = (deviceId, commandName) => {
    const device = devices.find(d => d._id === deviceId);
    const allCommands = device?.commands?.map(c => c.name) || [];

    setCommandPreferences(prev => {
      const newPrefs = JSON.parse(JSON.stringify(prev)); // Deep clone
      const currentPrefs = newPrefs[deviceId];

      // Si no hay preferencias guardadas, significa que todos están visibles
      // Al hacer clic, queremos quitar este comando (deseleccionar)
      if (!currentPrefs) {
        // Crear array con todos EXCEPTO el que se deseleccionó
        newPrefs[deviceId] = allCommands.filter(c => c !== commandName);
      } else {
        // Ya hay preferencias guardadas
        if (currentPrefs.includes(commandName)) {
          // Está seleccionado -> quitarlo
          newPrefs[deviceId] = currentPrefs.filter(c => c !== commandName);
        } else {
          // No está seleccionado -> agregarlo
          newPrefs[deviceId] = [...currentPrefs, commandName];
        }
      }

      // Si todos los comandos están seleccionados, eliminar la entrada
      // para volver al estado "mostrar todos por defecto"
      if (newPrefs[deviceId] && newPrefs[deviceId].length === allCommands.length) {
        delete newPrefs[deviceId];
      }

      return newPrefs;
    });
  };

  const selectAllCommands = (deviceId) => {
    setCommandPreferences(prev => {
      const newPrefs = { ...prev };
      delete newPrefs[deviceId]; // Sin preferencias = mostrar todos
      return newPrefs;
    });
  };

  const deselectAllCommands = (deviceId) => {
    setCommandPreferences(prev => {
      const newPrefs = { ...prev };
      newPrefs[deviceId] = []; // Array vacío = no mostrar ninguno
      return newPrefs;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/users/me/view-preferences', { viewPreferences, commandPreferences });
      if (onSave) {
        onSave({ viewPreferences, commandPreferences });
      }
      onClose();
    } catch (error) {
      console.error('Error saving preferences:', error);
      alert('Error al guardar preferencias');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  // Agrupar dispositivos por sitio
  const devicesBySite = devices.reduce((acc, device) => {
    const siteName = device.site?.name || device.siteId?.name || 'Sin sitio';
    if (!acc[siteName]) {
      acc[siteName] = [];
    }
    acc[siteName].push(device);
    return acc;
  }, {});

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#1f2937',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '700px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #374151',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, color: '#f9fafb', fontSize: '1.25rem' }}>
            Preferencias de Visualización
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#9ca3af',
              fontSize: '1.5rem',
              cursor: 'pointer',
              padding: '0.25rem'
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1rem 1.5rem'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>
              Cargando...
            </div>
          ) : devices.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>
              No tienes dispositivos asignados
            </div>
          ) : (
            <div>
              <p style={{ color: '#9ca3af', marginBottom: '1rem', fontSize: '0.875rem' }}>
                Selecciona qué variables y comandos quieres ver en el dashboard para cada dispositivo.
                Si no seleccionas ninguno, se mostrarán todos los disponibles.
              </p>

              {Object.entries(devicesBySite).map(([siteName, siteDevices]) => (
                <div key={siteName} style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{
                    color: '#60a5fa',
                    fontSize: '1rem',
                    marginBottom: '0.75rem',
                    borderBottom: '1px solid #374151',
                    paddingBottom: '0.5rem'
                  }}>
                    {siteName}
                  </h3>

                  {siteDevices.map(device => (
                    <div key={device._id} style={{
                      backgroundColor: '#374151',
                      borderRadius: '6px',
                      marginBottom: '0.75rem',
                      overflow: 'hidden'
                    }}>
                      {/* Device header */}
                      <div
                        onClick={() => toggleDevice(device._id)}
                        style={{
                          padding: '0.75rem 1rem',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer',
                          backgroundColor: '#4b5563'
                        }}
                      >
                        <span style={{ color: '#f9fafb', fontWeight: '500' }}>
                          {expandedDevices[device._id] ? '▼' : '▶'} {device.name}
                        </span>
                        <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                          {device.magnitudes?.length || 0} variables{device.commands?.length > 0 ? `, ${device.commands.length} comandos` : ''}
                        </span>
                      </div>

                      {/* Magnitudes list */}
                      {expandedDevices[device._id] && (
                        <div style={{ padding: '0.75rem 1rem' }}>
                          {/* Quick actions */}
                          <div style={{
                            display: 'flex',
                            gap: '0.5rem',
                            marginBottom: '0.75rem'
                          }}>
                            <button
                              onClick={() => selectAllMagnitudes(device._id)}
                              style={{
                                padding: '0.25rem 0.75rem',
                                fontSize: '0.75rem',
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              Todas
                            </button>
                            <button
                              onClick={() => deselectAllMagnitudes(device._id)}
                              style={{
                                padding: '0.25rem 0.75rem',
                                fontSize: '0.75rem',
                                backgroundColor: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              Ninguna
                            </button>
                          </div>

                          {/* Magnitudes checkboxes */}
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                            gap: '0.5rem'
                          }}>
                            {(device.magnitudes || []).map((magnitude, index) => {
                              const isSelected = isMagnitudeSelected(device._id, magnitude.name);
                              const uniqueKey = `${device._id}-${magnitude.name}-${index}`;
                              return (
                                <div
                                  key={uniqueKey}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleMagnitude(device._id, magnitude.name);
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    color: '#d1d5db',
                                    cursor: 'pointer',
                                    padding: '0.25rem',
                                    borderRadius: '4px',
                                    backgroundColor: isSelected
                                      ? 'rgba(16, 185, 129, 0.1)'
                                      : 'transparent',
                                    userSelect: 'none'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {}} // Manejado por onClick del div padre
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ cursor: 'pointer', pointerEvents: 'none' }}
                                  />
                                  <span style={{ fontSize: '0.875rem' }}>
                                    {magnitude.icon || ''} {magnitude.label || magnitude.name}
                                  </span>
                                </div>
                              );
                            })}
                          </div>

                          {(!device.magnitudes || device.magnitudes.length === 0) && (
                            <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
                              Este dispositivo no tiene variables configuradas
                            </p>
                          )}

                          {/* ===== SECCIÓN DE COMANDOS ===== */}
                          {device.commands && device.commands.length > 0 && (
                            <>
                              <div style={{
                                marginTop: '1rem',
                                paddingTop: '0.75rem',
                                borderTop: '1px solid #4b5563'
                              }}>
                                <h4 style={{
                                  color: '#f59e0b',
                                  fontSize: '0.875rem',
                                  marginBottom: '0.5rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem'
                                }}>
                                  🎮 Comandos
                                </h4>

                                {/* Quick actions para comandos */}
                                <div style={{
                                  display: 'flex',
                                  gap: '0.5rem',
                                  marginBottom: '0.75rem'
                                }}>
                                  <button
                                    onClick={() => selectAllCommands(device._id)}
                                    style={{
                                      padding: '0.25rem 0.75rem',
                                      fontSize: '0.75rem',
                                      backgroundColor: '#10b981',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Todos
                                  </button>
                                  <button
                                    onClick={() => deselectAllCommands(device._id)}
                                    style={{
                                      padding: '0.25rem 0.75rem',
                                      fontSize: '0.75rem',
                                      backgroundColor: '#ef4444',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    Ninguno
                                  </button>
                                </div>

                                {/* Commands checkboxes */}
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                                  gap: '0.5rem'
                                }}>
                                  {(device.commands || []).map((command, index) => {
                                    const isSelected = isCommandSelected(device._id, command.name);
                                    const uniqueKey = `${device._id}-cmd-${command.name}-${index}`;
                                    return (
                                      <div
                                        key={uniqueKey}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          toggleCommand(device._id, command.name);
                                        }}
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '0.5rem',
                                          color: '#d1d5db',
                                          cursor: 'pointer',
                                          padding: '0.25rem',
                                          borderRadius: '4px',
                                          backgroundColor: isSelected
                                            ? 'rgba(245, 158, 11, 0.1)'
                                            : 'transparent',
                                          userSelect: 'none'
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => {}} // Manejado por onClick del div padre
                                          onClick={(e) => e.stopPropagation()}
                                          style={{ cursor: 'pointer', pointerEvents: 'none' }}
                                        />
                                        <span style={{ fontSize: '0.875rem' }}>
                                          {command.icon || '🎮'} {command.label || command.name}
                                        </span>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '1rem 1.5rem',
          borderTop: '1px solid #374151',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#4b5563',
              color: '#f9fafb',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: saving ? '#6b7280' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
          >
            {saving ? 'Guardando...' : 'Guardar Preferencias'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserViewPreferences;
